import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  DOCUMENT
, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';
import { defaultTicketIcon, ticketIconsMap } from '../../../../constants/ticket-icons.constant';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-automatic-distribution-osm-map',
  templateUrl: './automatic-distribution-osm-map.component.html',
  styleUrls: ['./automatic-distribution-osm-map.component.scss'],
  standalone: false,
})
export class AutomaticDistributionOsmMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static scriptPromise: Promise<any> = null;
  private static clusterScriptPromise: Promise<void> = null;

  @Input() routeOrders: any[] = [];
  @Input() unassignedOrders: any[] = [];
  @Input() routeLines: any[] = [];
  @Input() startMarkers: any[] = [];
  @Input() coordinates: number[][] = [];
  @Input() tickets;
  @Input() resolveCoordinates: (item) => number[];
  @Input() renderKey = 0;

  @ViewChild('mapContainer') mapContainer: ElementRef;

  public loading = true;
  public errorText = '';

  private leaflet;
  private map;
  private objectsLayer;
  private orderMarkersLayer;
  private renderedKey: number | null = null;
  private destroyed = false;
  private moment = require('moment');

  constructor(@Inject(DOCUMENT) private document: Document, private router: Router) {}

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.map && changes.renderKey && changes.renderKey.currentValue !== this.renderedKey) {
      this.renderedKey = changes.renderKey.currentValue;
      this.renderMapObjects();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initializeMap(): void {
    this.loadLeaflet().then(
      (leaflet) => {
        this.loadMarkerCluster(leaflet)
          .catch(() => undefined)
          .then(() => this.createMap(leaflet));
      },
      () => {
        if (!this.destroyed) {
          this.loading = false;
          this.errorText = 'Не удалось загрузить OpenStreetMap.';
        }
      }
    );
  }

  private createMap(leaflet): void {
    if (this.destroyed || !this.mapContainer) {
      return;
    }

    this.leaflet = leaflet;
    this.map = leaflet.map(this.mapContainer.nativeElement, {
      center: [55.76, 37.64],
      zoom: 10,
      zoomControl: true,
    });
    this.map.attributionControl.setPrefix(false);
    leaflet
      .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      })
      .addTo(this.map);
    this.objectsLayer = leaflet.layerGroup().addTo(this.map);
    this.orderMarkersLayer = leaflet.markerClusterGroup
      ? leaflet.markerClusterGroup({
          showCoverageOnHover: false,
          zoomToBoundsOnClick: false,
          spiderfyOnMaxZoom: false,
        })
      : leaflet.layerGroup();
    this.orderMarkersLayer.addTo(this.map);

    if (leaflet.markerClusterGroup) {
      this.orderMarkersLayer.on('clusterclick', (event) => this.openClusterPopup(event.layer));
    }

    this.renderedKey = this.renderKey;
    this.loading = false;
    this.renderMapObjects();
  }

  private loadMarkerCluster(leaflet): Promise<void> {
    if (leaflet.markerClusterGroup) {
      return Promise.resolve();
    }

    if (AutomaticDistributionOsmMapComponent.clusterScriptPromise) {
      return AutomaticDistributionOsmMapComponent.clusterScriptPromise;
    }

    this.ensureMarkerClusterStyles();
    AutomaticDistributionOsmMapComponent.clusterScriptPromise = new Promise((resolve, reject) => {
      const existingScript = this.document.querySelector('script[data-leaflet-marker-cluster]');
      const script = (existingScript as HTMLScriptElement) || this.document.createElement('script');

      script.onload = () => (leaflet.markerClusterGroup ? resolve() : reject(new Error('Leaflet MarkerCluster is unavailable')));
      script.onerror = () => reject(new Error('Failed to load Leaflet MarkerCluster'));

      if (!existingScript) {
        script.src = 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-leaflet-marker-cluster', 'true');
        this.document.head.appendChild(script);
      }
    });

    return AutomaticDistributionOsmMapComponent.clusterScriptPromise;
  }

  private ensureMarkerClusterStyles(): void {
    [
      {
        attribute: 'data-leaflet-marker-cluster-css',
        href: 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/MarkerCluster.css',
      },
      {
        attribute: 'data-leaflet-marker-cluster-default-css',
        href: 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css',
      },
    ].forEach((style) => {
      if (this.document.querySelector(`link[${style.attribute}]`)) {
        return;
      }

      const link = this.document.createElement('link');

      link.rel = 'stylesheet';
      link.href = style.href;
      link.setAttribute(style.attribute, 'true');
      this.document.head.appendChild(link);
    });
  }

  private loadLeaflet(): Promise<any> {
    const windowWithLeaflet = window as any;

    if (windowWithLeaflet.L) {
      return Promise.resolve(windowWithLeaflet.L);
    }

    if (AutomaticDistributionOsmMapComponent.scriptPromise) {
      return AutomaticDistributionOsmMapComponent.scriptPromise;
    }

    this.ensureLeafletStyles();
    AutomaticDistributionOsmMapComponent.scriptPromise = new Promise((resolve, reject) => {
      const existingScript = this.document.querySelector('script[data-leaflet-api]');
      const script = (existingScript as HTMLScriptElement) || this.document.createElement('script');

      script.onload = () => {
        const loadedLeaflet = (window as any).L;

        return loadedLeaflet ? resolve(loadedLeaflet) : reject(new Error('Leaflet is unavailable'));
      };
      script.onerror = () => reject(new Error('Failed to load Leaflet'));

      if (!existingScript) {
        script.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.defer = true;
        script.setAttribute('data-leaflet-api', 'true');
        this.document.head.appendChild(script);
      }
    });

    return AutomaticDistributionOsmMapComponent.scriptPromise;
  }

  private ensureLeafletStyles(): void {
    if (this.document.querySelector('link[data-leaflet-css]')) {
      return;
    }

    const link = this.document.createElement('link');

    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
    link.setAttribute('data-leaflet-css', 'true');
    this.document.head.appendChild(link);
  }

  private renderMapObjects(): void {
    if (!this.map || !this.objectsLayer || !this.orderMarkersLayer) {
      return;
    }

    this.objectsLayer.clearLayers();
    this.orderMarkersLayer.clearLayers();

    (this.routeLines || []).forEach((line) => {
      const coordinates = this.reduceCoordinates((line && line.coordinates) || [], 1000).filter(
        (coordinate) => Array.isArray(coordinate) && coordinate.length >= 2
      );

      if (coordinates.length < 2) {
        return;
      }

      this.leaflet
        .polyline(coordinates, {
          color: line.strokeColor || '#2684ff',
          opacity: Number(line.strokeOpacity) || 1,
          weight: Number(line.strokeWidth) || 5,
        })
        .addTo(this.objectsLayer);
    });

    (this.startMarkers || []).forEach((marker) => this.addStartMarker(marker));
    (this.routeOrders || []).forEach((item) => this.addOrderMarker(item));
    (this.unassignedOrders || []).forEach((item) => this.addOrderMarker(item));

    this.fitMapToCoordinates();
  }

  getOrderSequence(item): number | null {
    if (!item || item.unassigned) {
      return null;
    }
    const sequence = Number(item.order && item.order.sequence);
    if (Number.isInteger(sequence) && sequence > 0) {
      return sequence;
    }
    return Number.isInteger(item.orderIndex) && item.orderIndex >= 0 ? item.orderIndex + 1 : null;
  }

  private addOrderMarker(item): void {
    const coordinate = this.getItemCoordinates(item);

    if (!coordinate) {
      return;
    }

    const route = item && item.route;
    const ticket = this.getTicket(item && item.order);
    const foundTicketIcon = ticketIconsMap.find(
      (ticketIcon) => ticket && ticket.ticket_type && ticketIcon.id === ticket.ticket_type.id
    );
    const ticketIcon = foundTicketIcon || defaultTicketIcon;
    const sequence = this.getOrderSequence(item);
    const employeeLabel = item && item.unassigned ? 'Не распределено' : (route && route.employee_name) || 'Сотрудник';
    const label = sequence === null ? employeeLabel : `№${sequence} · ${employeeLabel}`;
    const marker = this.leaflet.marker(coordinate, {
      icon: this.createOrderDivIcon(label, item && item.unassigned ? ticketIcon.pathGrey : ticketIcon.path),
      keyboard: true,
      title: label,
      employeeLabel: employeeLabel,
      employeeId: route && route.employee_id,
      routeSequence: sequence,
      ticketTypeLabel: ticketIcon.name || 'Заявка',
      ticketNumber: this.getOrderNumber(item && item.order),
    });

    marker.bindPopup(this.createOrderPopup(item), {
      autoPan: true,
      autoPanPadding: [24, 24],
      closeButton: true,
      maxWidth: 380,
      minWidth: 300,
    });
    marker.addTo(this.orderMarkersLayer);
  }

  private createOrderDivIcon(label: string, iconUrl: string) {
    const safeLabel = this.escapeHtml(label);
    const safeIconUrl = this.escapeHtml(iconUrl);

    return this.leaflet.divIcon({
      className: '',
      html: `
        <div style="height:35px;position:relative;white-space:nowrap;">
          <img src="${safeIconUrl}" alt="" style="display:block;height:35px;width:26px;">
          <span style="background:#fff;border-radius:10px;color:#000;font:700 14px/18px Arial;left:30px;padding:5px;position:absolute;top:5px;">${safeLabel}</span>
        </div>
      `,
      iconAnchor: [14, 24],
      iconSize: [Math.max(120, 40 + label.length * 8), 35],
    });
  }

  private addStartMarker(markerData): void {
    const coordinate = markerData && markerData.coordinates;

    if (!Array.isArray(coordinate) || coordinate.length < 2) {
      return;
    }

    const marker = this.leaflet.marker(coordinate, {
      icon: this.createDivIcon('С', 'Старт', '#172b4d', '#ffcc00'),
      keyboard: true,
      title: 'Старт маршрута',
    });

    marker.bindPopup(this.createStartPopup(markerData), {
      autoPan: true,
      autoPanPadding: [24, 24],
      closeButton: true,
      maxWidth: 380,
      minWidth: 300,
    });
    marker.addTo(this.objectsLayer);
  }

  private createDivIcon(letter: string, label: string, color: string, borderColor = '#ffffff') {
    const safeLetter = this.escapeHtml(letter);
    const safeLabel = this.escapeHtml(label);

    return this.leaflet.divIcon({
      className: '',
      html: `
        <div style="display:flex;align-items:center;white-space:nowrap;">
          <div style="align-items:center;background:${color};border:3px solid ${borderColor};border-radius:50%;box-shadow:0 1px 4px rgba(9,30,66,.35);color:#fff;display:flex;font:700 13px Arial;height:28px;justify-content:center;width:28px;">
            ${safeLetter}
          </div>
          <span style="background:rgba(255,255,255,.94);border-radius:10px;color:#172b4d;font:700 12px/18px Arial;margin-left:4px;padding:1px 7px;">
            ${safeLabel}
          </span>
        </div>
      `,
      iconAnchor: [14, 28],
      iconSize: [Math.max(100, 42 + label.length * 7), 28],
    });
  }

  private createOrderPopup(item): HTMLElement {
    const order = (item && item.order) || {};
    const route = (item && item.route) || {};
    const content = this.createPopupElement();

    this.appendPopupTitle(content, order.order_name || 'Заявка');
    this.appendPopupOrderLink(content, order);

    if (item && item.unassigned) {
      this.appendPopupRow(content, 'Не распределено', '', '#bf2600');
    } else {
      this.appendPopupRow(content, 'Сотрудник:', route.employee_name || '-');
      const sequence = this.getOrderSequence(item);
      if (sequence !== null) {
        this.appendPopupRow(content, 'Порядок в маршруте (план):', String(sequence));
      }
    }

    if (order.arrival_time) {
      this.appendPopupRow(content, 'Прибытие:', this.formatTime(order.arrival_time));
    }

    if (order.work_start_time || order.work_end_time) {
      this.appendPopupRow(content, 'Работы:', `${this.formatTime(order.work_start_time)} - ${this.formatTime(order.work_end_time)}`);
    }

    return content;
  }

  private openClusterPopup(cluster): void {
    if (!cluster || !this.map) {
      return;
    }

    const content = this.document.createElement('div');
    const tickets = cluster.getAllChildMarkers().slice().sort((a, b) => {
      const employeeA = String(a.options.employeeId || '');
      const employeeB = String(b.options.employeeId || '');
      return employeeA.localeCompare(employeeB) ||
        (a.options.routeSequence || Infinity) - (b.options.routeSequence || Infinity);
    });

    content.className = 'automatic-osm-map__cluster-popup';

    tickets.forEach((marker) => {
      const row = this.document.createElement('button');
      const employee = this.document.createElement('span');
      const ticketDetails = this.document.createElement('span');
      const ticketTypeLabel = marker.options.ticketTypeLabel || 'Заявка';
      const ticketNumber = marker.options.ticketNumber && marker.options.ticketNumber !== '-'
        ? `№${marker.options.ticketNumber}`
        : '';

      row.type = 'button';
      row.className = 'automatic-osm-map__cluster-ticket';
      employee.textContent = marker.options.employeeLabel || marker.options.title || 'Заявка';
      employee.className = 'automatic-osm-map__cluster-employee';
      const sequenceLabel = marker.options.routeSequence
        ? `План: №${marker.options.routeSequence} в маршруте`
        : '';
      ticketDetails.textContent = [sequenceLabel, ticketTypeLabel, ticketNumber].filter(Boolean).join(' · ');
      ticketDetails.className = 'automatic-osm-map__cluster-details';
      row.appendChild(employee);
      row.appendChild(ticketDetails);
      row.addEventListener('click', () => {
        this.map.closePopup();
        this.map.setView(marker.getLatLng(), 18);
        marker.getPopup().setLatLng(marker.getLatLng()).openOn(this.map);
      });
      content.appendChild(row);
    });

    this.leaflet
      .popup({
        autoPan: true,
        autoPanPadding: [24, 24],
        closeButton: true,
        maxWidth: 360,
        minWidth: 260,
      })
      .setLatLng(cluster.getLatLng())
      .setContent(content)
      .openOn(this.map);
  }

  private createStartPopup(markerData): HTMLElement {
    const content = this.createPopupElement();

    this.appendPopupTitle(content, 'Старт маршрута');
    this.appendPopupRow(content, 'Координаты:', (markerData && markerData.coordinateText) || '-');
    ((markerData && markerData.routes) || []).forEach((route) => {
      this.appendPopupRow(content, route.name || 'Сотрудник', `(${route.totalOrders || 0} заявок)`);
    });

    return content;
  }

  private createPopupElement(): HTMLElement {
    const content = this.document.createElement('div');

    content.style.color = '#172b4d';
    content.style.fontFamily = 'Arial, sans-serif';
    content.style.fontSize = '13px';
    content.style.lineHeight = '18px';
    content.style.whiteSpace = 'nowrap';

    return content;
  }

  private appendPopupTitle(content: HTMLElement, title: string): void {
    const element = this.document.createElement('div');

    element.textContent = title;
    element.style.fontSize = '16px';
    element.style.fontWeight = '700';
    element.style.marginBottom = '8px';
    content.appendChild(element);
  }

  private appendPopupOrderLink(content: HTMLElement, order): void {
    const row = this.document.createElement('div');
    const label = this.document.createElement('span');
    const link = this.document.createElement('a');
    const ticketId = order && (order.order_id || order.id);
    const orderNumber = this.getOrderNumber(order);
    const url = ticketId ? '/' + TICKETS_PATHS.ticketLink + '/' + ticketId : '';

    label.textContent = 'Номер заявки: ';
    link.textContent = `№ ${orderNumber}`;
    link.style.color = '#0052cc';
    link.style.cursor = 'pointer';
    link.style.textDecoration = 'underline';

    if (url) {
      link.href = url;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.router.navigate([url]);
      });
    }

    row.appendChild(label);
    row.appendChild(link);
    content.appendChild(row);
  }

  private appendPopupRow(content: HTMLElement, labelText: string, valueText: string, color?: string): void {
    const row = this.document.createElement('div');

    row.textContent = labelText + (valueText ? ` ${valueText}` : '');
    row.style.marginTop = '6px';

    if (color) {
      row.style.color = color;
    }

    content.appendChild(row);
  }

  private getItemCoordinates(item): number[] | null {
    if (this.resolveCoordinates) {
      return this.resolveCoordinates(item);
    }

    const order = item && item.order;
    const latitude = Number(order && (order.latitude || order.lat));
    const longitude = Number(order && (order.longitude || order.lon || order.lng));

    return Number.isFinite(latitude) && Number.isFinite(longitude) ? [latitude, longitude] : null;
  }

  private fitMapToCoordinates(): void {
    const coordinates = (this.coordinates || []).filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2);

    if (!coordinates.length) {
      return;
    }

    if (coordinates.length === 1) {
      this.map.setView(coordinates[0], 14);
      return;
    }

    this.map.fitBounds(this.leaflet.latLngBounds(coordinates), {
      padding: [36, 36],
    });
  }

  private getOrderNumber(order): string {
    const ticket = this.getTicket(order);
    const value = (ticket && ticket.hd_ticket_number) || (order && (order.hd_ticket_number || order.order_id));

    return value ? String(value) : '-';
  }

  private getTicket(order) {
    const orderId = Number(order && (order.order_id || order.id));
    const tickets = this.tickets && this.tickets.dataFlatList;

    if (!Number.isFinite(orderId) || !tickets) {
      return null;
    }

    return tickets.find((ticket) => Number(ticket && ticket.id) === orderId) || null;
  }

  private formatTime(value): string {
    return value ? this.moment(value).format('HH:mm') : '-';
  }

  private reduceCoordinates(coordinates: any[], limit: number): any[] {
    if (!coordinates || coordinates.length <= limit) {
      return coordinates || [];
    }

    const reduced = [];
    const lastIndex = coordinates.length - 1;
    const step = lastIndex / (limit - 1);

    for (let index = 0; index < limit; index++) {
      reduced.push(coordinates[Math.min(lastIndex, Math.round(index * step))]);
    }

    return reduced;
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
